import { apiUrl } from "../ApiManager/AxiosHeaders";
import { getBranchAgencyName } from "./jdConstants";

const MULTIPART_HEADERS = { "Content-Type": "multipart/form-data" };

const getLatestRecord = (records = []) => {
  if (!records?.length) return null;
  return [...records].sort(
    (a, b) => new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime(),
  )[0];
};

export function flattenActiveEmployees(grouped = []) {
  return grouped.flatMap((group) => group.users || []);
}

export async function fetchActiveEmployees(params = {}) {
  const response = await apiUrl.get("/active-employees", { params });
  const payload = response?.data || {};

  if (!payload.success) {
    throw new Error(payload.message || "Failed to load active employees.");
  }

  const grouped = payload.data || [];

  return {
    grouped,
    totalUsers: payload.total_users || 0,
    totalBranches: payload.total_branches || 0,
    users: flattenActiveEmployees(grouped),
  };
}

export function buildJdRegistryFromActiveEmployees(activeUsers = [], branches = []) {
  return activeUsers.map((user) => {
    const { branchId, branch } = resolveUserBranchMeta(user, branches);
    const latestJd = getLatestRecord(user.job_descriptions);

    if (latestJd) {
      return {
        id: latestJd.id,
        user_id: user.id,
        name: user.name || "—",
        branch_id: branchId || (user.branch_id ? String(user.branch_id) : ""),
        branch: user.branch_name || branch,
        job_title: latestJd.job_title || "",
        status: "uploaded",
        original_file_name: latestJd.file_name,
        file_type: latestJd.file_extension,
        file_url: latestJd.file_url,
        file_size: latestJd.file_size,
        updated_at: latestJd.updated_at,
      };
    }

    return {
      id: `missing-${user.id}`,
      user_id: user.id,
      name: user.name || "—",
      branch_id: branchId || (user.branch_id ? String(user.branch_id) : ""),
      branch: user.branch_name || branch,
      job_title: "",
      status: "missing",
      original_file_name: null,
      file_type: null,
      file_url: null,
      updated_at: null,
    };
  });
}

export function buildContractRegistryFromActiveEmployees(activeUsers = [], branches = []) {
  return activeUsers.map((user) => {
    const { branchId, branch } = resolveUserBranchMeta(user, branches);
    const latestContract = getLatestRecord(user.contracts);

    if (latestContract) {
      return {
        id: latestContract.id,
        user_id: user.id,
        name: user.name || "—",
        branch_id: branchId || (user.branch_id ? String(user.branch_id) : ""),
        branch: user.branch_name || branch,
        start_date: latestContract.start_date,
        end_date: latestContract.end_date,
        original_file_name: latestContract.file_name,
        file_type: latestContract.file_extension,
        file_url: latestContract.file_url,
        file_size: latestContract.file_size,
        updated_at: latestContract.updated_at,
      };
    }

    return {
      id: `missing-${user.id}`,
      user_id: user.id,
      name: user.name || "—",
      branch_id: branchId || (user.branch_id ? String(user.branch_id) : ""),
      branch: user.branch_name || branch,
      start_date: null,
      end_date: null,
      original_file_name: null,
      file_type: null,
      file_url: null,
      updated_at: null,
    };
  });
}

export async function fetchAllPaginatedRecords(endpoint) {
  let page = 1;
  let all = [];
  let lastPage = 1;

  do {
    const response = await apiUrl.get(endpoint, { params: { page, perPage: 100 } });
    const payload = response?.data || {};
    all = all.concat(payload.data || []);
    lastPage = payload.pagination?.lastPage || 1;
    page += 1;
  } while (page <= lastPage);

  return all;
}

export function resolveUserBranchMeta(user, branches = []) {
  const branchId = user?.main_branch || user?.branch_id || "";
  if (user?.branch_name) {
    return {
      branchId: branchId ? String(branchId) : user?.branch_id ? String(user.branch_id) : "",
      branch: user.branch_name,
    };
  }
  const branch = branches.find((item) => String(item.id) === String(branchId));
  return {
    branchId: branchId ? String(branchId) : "",
    branch: getBranchAgencyName(branch) || "—",
  };
}

export function mapJdApiRecord(record, allUsers = [], branches = []) {
  const user =
    record.user || allUsers.find((item) => String(item.id) === String(record.user_id));
  const { branchId, branch } = resolveUserBranchMeta(user, branches);

  return {
    id: record.id,
    user_id: record.user_id,
    name: user?.name || record.user?.name || "—",
    branch_id: branchId,
    branch,
    job_title: record.job_title || "",
    status: "uploaded",
    original_file_name: record.file_name,
    file_type: record.file_extension,
    file_url: record.file_url,
    file_size: record.file_size,
    updated_at: record.updated_at,
  };
}

export function mapContractApiRecord(record, allUsers = [], branches = []) {
  const user =
    record.user || allUsers.find((item) => String(item.id) === String(record.user_id));
  const { branchId, branch } = resolveUserBranchMeta(user, branches);

  return {
    id: record.id,
    user_id: record.user_id,
    name: user?.name || record.user?.name || "—",
    branch_id: branchId,
    branch,
    start_date: record.start_date,
    end_date: record.end_date,
    original_file_name: record.file_name,
    file_type: record.file_extension,
    file_url: record.file_url,
    file_size: record.file_size,
    updated_at: record.updated_at,
  };
}

export function mergeJdRegistryRows(apiRecords, allUsers = [], branches = []) {
  const byUserId = new Map();

  apiRecords.forEach((record) => {
    const mapped = mapJdApiRecord(record, allUsers, branches);
    const key = String(mapped.user_id);
    const existing = byUserId.get(key);
    if (
      !existing ||
      new Date(mapped.updated_at || 0).getTime() > new Date(existing.updated_at || 0).getTime()
    ) {
      byUserId.set(key, mapped);
    }
  });

  allUsers.forEach((user) => {
    const key = String(user.id);
    if (byUserId.has(key)) return;

    const { branchId, branch } = resolveUserBranchMeta(user, branches);
    byUserId.set(key, {
      id: `missing-${user.id}`,
      user_id: user.id,
      name: user.name || "—",
      branch_id: branchId,
      branch,
      job_title: "",
      status: "missing",
      original_file_name: null,
      file_type: null,
      file_url: null,
      updated_at: null,
    });
  });

  return Array.from(byUserId.values());
}

export function mergeContractRegistryRows(apiRecords, allUsers = [], branches = []) {
  const byUserId = new Map();

  apiRecords.forEach((record) => {
    const mapped = mapContractApiRecord(record, allUsers, branches);
    const key = String(mapped.user_id);
    const existing = byUserId.get(key);
    if (
      !existing ||
      new Date(mapped.updated_at || 0).getTime() > new Date(existing.updated_at || 0).getTime()
    ) {
      byUserId.set(key, mapped);
    }
  });

  allUsers.forEach((user) => {
    const key = String(user.id);
    if (byUserId.has(key)) return;

    const { branchId, branch } = resolveUserBranchMeta(user, branches);
    byUserId.set(key, {
      id: `missing-${user.id}`,
      user_id: user.id,
      name: user.name || "—",
      branch_id: branchId,
      branch,
      start_date: null,
      end_date: null,
      original_file_name: null,
      file_type: null,
      file_url: null,
      updated_at: null,
    });
  });

  return Array.from(byUserId.values());
}

export function isPersistedRegistryId(id) {
  return id != null && !String(id).startsWith("missing-");
}

export async function uploadEmployeeJobDescription({ userId, jobTitle, file, existingId }) {
  const formData = new FormData();
  formData.append("user_id", userId);
  formData.append("job_title", jobTitle);

  if (existingId) {
    if (file) formData.append("file", file);
    return apiUrl.post(`/employee-job-descriptions/${existingId}/update`, formData, {
      headers: MULTIPART_HEADERS,
    });
  }

  if (!file) {
    throw new Error("File is required for new job description upload.");
  }

  formData.append("file", file);
  return apiUrl.post("/employee-job-descriptions", formData, {
    headers: MULTIPART_HEADERS,
  });
}

export async function uploadEmployeeContract({
  userId,
  startDate,
  endDate,
  file,
  existingId,
}) {
  const formData = new FormData();
  formData.append("user_id", userId);
  formData.append("start_date", startDate);
  if (endDate) formData.append("end_date", endDate);

  if (existingId) {
    if (file) formData.append("file", file);
    return apiUrl.post(`/employee-contracts/${existingId}/update`, formData, {
      headers: MULTIPART_HEADERS,
    });
  }

  if (!file) {
    throw new Error("File is required for new contract upload.");
  }

  formData.append("file", file);
  return apiUrl.post("/employee-contracts", formData, {
    headers: MULTIPART_HEADERS,
  });
}

export async function deleteEmployeeJobDescription(id) {
  return apiUrl.post(`/employee-job-descriptions/${id}/delete`);
}

export async function deleteEmployeeContract(id) {
  return apiUrl.post(`/employee-contracts/${id}/delete`);
}

export async function fetchEmployeeJobDescription(id) {
  return apiUrl.get(`/employee-job-descriptions/${id}`);
}

export async function fetchEmployeeContract(id) {
  return apiUrl.get(`/employee-contracts/${id}`);
}
